import deepmerge from 'deepmerge'
import langLocalNames from './lang/lang_list'
import { loadLocaleMessages } from './lang/index'

const defaultCampaign = {
  language: {
    defaultLanguage: {
      lang: 'English',
      langCode: 'en',
    },
    optimizedLanguageList: [],
  },
  i18n: {},
  flags: {
    backgroundLayer: false,
    showMultilingual: false,
    isWeatherWidget: false,
    isCovid: false,
    isConsent: false,
    isRedirect: false,
    isAdvertising: false,
    adsPreLogin: false,
    adsInterstitial: false,
    adsThankYou: false,
    lazyLanguageBar: true,
    loginBtnIcons: true,
    primaryBtnIcon: true,
    singleLangCalculatesUserLang: false,
  },
  colors: {
    textColor: '#202020',
    subTitleTextColor: '#202020',
    footerTextColor: '#202020',
    supportTextColor: '#202020',
    background: '#FFFFFF',
    primaryBtn: '#202020',
    primaryBtnText: '#FFFFFF',
    layerColor: '#FFFFFFB0',
    loginBtns: {
      emailBtn: '#FFA500',
      freeWifiBtn: '#7f017f',
      smsBtn: '#1ABC9C',
      creditcardBtn: '#636363',
      passcodeBtn: '#636363',
      loginBtnText: '#FFFFFF',
      passcodeV2Btn: '#636363',
    },
    passcodeV2: {
      buttonBorderColor: '#636363',
      buttonTextColor: '#FFFFFF',
      buttonBackgroundColor: '#636363',
    },
  },
  sizes: {
    loginBtnsRadius: '32px',
    titleTextSize: '24px',
    subTitleTextSize: '16px',
    footerTextSize: '16px',
    supportTextSize: '16px',
    footerImg: '100%',
    headerImg: '100%',
    logoImg: '100%',
    backgroundImg: '345px',
  },
  fonts: {
    alignments: {
      loginBtns: 'left',
      primaryBtn: 'left',
    },
    justifyContent: {
      thankYouPage: 'space-between',
    },
    font: "'Avenir Next', sans-serif",
  },
  images: {
    background: '',
    logo: '',
    footer: '',
    header: [],
    backgroundRepeat: 'repeat',
    backgroundRepeatAlignment: 'center center',
  },
  layouts: {
    main: 'default',
    appLayout: 'default',
  },
  advertisement: {
    preLogin: {
      integrationId: null,
      integrationType: 'samba',
      skipEnabled: false,
      skipDelay: 5,
    },
    interstitial: {
      top: null,
      duration: 5,
    },
    postLogin: {
      top: null,
      middle: null,
      bottom: null,
    },
  },
  advancedOptions: {
    country: {
      name: 'United States',
      flag: '🇺🇸',
      code: 'US',
      dialCode: '+1',
    },
    customTermsOfService: '',
    scripts: {
      fbPixelId: '',
      googleAnalytics: '',
      footer: '',
      conversionPixel: '',
      head: '',
      customCSS: '',
    },
  },
  postLogin: {
    thankYouPage: {
      order: ['Like Box', 'Custom Text', 'Add Image', 'Add HTML'],
      options: [],
    },
    redirect: {
      options: {},
      redirectTime: 5,
    },
  },
  widgets: {
    weather: {
      unit: 'imperial',
    },
  },
}

const defaultConnectionCheckConfig = {
  enabled: true,
  maxAttempts: 5,
}

function replacePredefinedVariables(text) {
  if (typeof text !== 'string') {
    return text
  }

  return text.replace('{%YEAR%}', new Date().getFullYear())
}
function hydrateCampaign(defaultCampaign, campaignInfo) {
  const params = {
    destination: defaultCampaign,
    source: campaignInfo,
  }

  const overwriteMerge = (destinationArray, sourceArray, options) => sourceArray
  const deepMerge = deepmerge(params.destination, params.source, {
    arrayMerge: overwriteMerge,
  })
  return deepMerge
}

function hydrateI18n(data) {
  const localMessages = loadLocaleMessages()

  const campaignI18n = data.i18n || {}
  const canBeNull = [
    'sub_title_text',
    'footer_text',
    'email',
    'freewifi',
    'passcode',
    'sms',
    'credit_card',
  ]
  const customisableKeys = new Set([
    'title_text',
    'sub_title_text',
    'footer_text',
    'support',
    'agree',
    'covid_question',
    'covid_title',
    'covid_error',
    'covid_success',
    'email',
    'freewifi',
    'passcode',
    'sms',
    'credit_card',
    'consent_opt_in_text',
    'consent_opt_in_description',
    'consent_yes_text',
    'consent_no_text',
    'enjoy_browsing',
    'placeholder_passcode',
  ])
  const langCodes = Object.keys(campaignI18n)
  if (langCodes.length === 0) {
    langCodes.push(data.language.defaultLanguage.langCode)
  }
  const localLanguages = {}

  for (const code of langCodes) {
    localLanguages[code] = { ...localMessages[code] }

    const filteredData = Object.fromEntries(
      Object.entries(localLanguages[code]).filter(([key]) => customisableKeys.has(key)),
    )

    localLanguages[code] = {}
    localLanguages[code] = filteredData

    for (const key in localLanguages[code]) {
      if (canBeNull.includes(key)) {
        localLanguages[code][key] = null
      }
    }
  }
  for (const code of langCodes) {
    if (Object.prototype.hasOwnProperty.call(campaignI18n, code)) {
      for (const key in localLanguages[code]) {
        if (Object.prototype.hasOwnProperty.call(campaignI18n[code], key)) {
          localLanguages[code][key] = campaignI18n[code][key]
        }
      }

      // Spread dynamic keys directly into the main language object
      if (campaignI18n[code].dynamic) {
        localLanguages[code] = {
          ...localLanguages[code],
          ...campaignI18n[code].dynamic,
        }
      }
    }
  }
  return localLanguages
}

function setCampaignDefaults(campaignInfo) {
  // lets hydrate with default Values for missing keys
  campaignInfo = hydrateCampaign(defaultCampaign, campaignInfo)
  campaignInfo.i18n = hydrateI18n(campaignInfo)

  if (!campaignInfo.loginOptions) {
    campaignInfo.loginOptions = {}
  }

  if (!campaignInfo.loginOptions.options || !Array.isArray(campaignInfo.loginOptions.options)) {
    campaignInfo.loginOptions.options = []
  }

  let order = campaignInfo.loginOptions.order || [
    'Email' | 'Free Wi-Fi' | 'Passcode' | 'Credit Card' | 'SMS',
  ]
  const options = campaignInfo.loginOptions.options

  const optionsNames = options.map((a) => a.name)
  order = order.filter((item) => optionsNames.includes(item))
  if (options.length > order.length) {
    const orderSet = new Set(order)
    options.forEach((option) => {
      if (!orderSet.has(option.name)) {
        order.push(option.name)
      }
    })
  }
  let socialMediaOrder = order.join('&')
  socialMediaOrder = socialMediaOrder.replace('Free Wi-Fi', 'freewifi')
  socialMediaOrder = socialMediaOrder.toLowerCase()
  campaignInfo.socialMediaOrder = socialMediaOrder

  if (campaignInfo.loginOptions.customFields && !campaignInfo?.development?.preview) {
    if (!campaignInfo.loginOptions.customFields) {
      campaignInfo.customFields = []
    } else {
      campaignInfo.customFields = campaignInfo.loginOptions.customFields
    }
  }

  // Ensure postLogin.thankYouPage.options exists before checking length
  if (!campaignInfo.postLogin?.thankYouPage?.options) {
    if (!campaignInfo.postLogin) campaignInfo.postLogin = {}
    if (!campaignInfo.postLogin.thankYouPage) campaignInfo.postLogin.thankYouPage = {}
    campaignInfo.postLogin.thankYouPage.options = []
  }

  // incase if thankyou option is not available
  if (campaignInfo.postLogin.thankYouPage.options.length === 0) {
    const data = {
      action: 'Custom Text',
      icon: 'fa-pencil',
      text: 'Enjoy browsing...',
    }
    campaignInfo.postLogin.thankYouPage.options.push(data)
  }

  // const socialMediaOptions = {}
  // if (campaignInfo.loginOptions.options && campaignInfo.loginOptions.options.length > 0) {
  //   campaignInfo.loginOptions.options.forEach((o) => {
  //     socialMediaOptions[o.name] = o
  //   })
  // }

  // campaignInfo.socialMediaOptions = socialMediaOptions

  campaignInfo.flags.lazyLanguageBar = !Object.prototype.hasOwnProperty.call(
    campaignInfo.flags,
    'lazyLanguageBar',
  )
    ? false
    : campaignInfo.flags.lazyLanguageBar

  const advertisement = campaignInfo.advertisement ?? {}
  const advertisingEnabled = Boolean(campaignInfo.flags?.isAdvertising)
  const hasPreLoginAds = Boolean(
    advertisingEnabled && advertisement.preLogin && advertisement.preLogin.integrationId,
  )
  const hasInterstitialAds = Boolean(
    advertisingEnabled && advertisement.interstitial && advertisement.interstitial.top?.id,
  )
  const hasThankYouAds = Boolean(
    advertisingEnabled &&
      advertisement.postLogin &&
      (advertisement.postLogin.top?.id ||
        advertisement.postLogin.middle?.id ||
        advertisement.postLogin.bottom?.id),
  )
  if (hasPreLoginAds && !Object.prototype.hasOwnProperty.call(campaignInfo.flags, 'adsPreLogin')) {
    campaignInfo.flags.adsPreLogin = true
  }
  if (
    hasInterstitialAds &&
    !Object.prototype.hasOwnProperty.call(campaignInfo.flags, 'adsInterstitial')
  ) {
    campaignInfo.flags.adsInterstitial = true
  }
  if (hasThankYouAds && !Object.prototype.hasOwnProperty.call(campaignInfo.flags, 'adsThankYou')) {
    campaignInfo.flags.adsThankYou = true
  }

  if (campaignInfo.language.optimizedLanguageList.length > 1) {
    const allLanguages = Object.keys(langLocalNames).map((code) => ({
      langCode: code,
      lang: langLocalNames[code],
    }))
    const result = campaignInfo.language.optimizedLanguageList
      .map((code) => {
        return allLanguages.find((language) => language.langCode === code)
      })
      .filter(Boolean)
    campaignInfo.language.optimizedLanguageList = result
  }

  campaignInfo.backButton = true

  const smsOption =
    campaignInfo.loginOptions && Array.isArray(campaignInfo.loginOptions.options)
      ? campaignInfo.loginOptions.options.find((opt) => opt.name === 'SMS')
      : null
  if (smsOption) {
    if (!smsOption.fields) smsOption.fields = []
    if (!smsOption.fields.includes('Phone Number') && !smsOption.fields.includes('Phone')) {
      smsOption.fields.push('Phone')
    }
  }
  return campaignInfo
}

// Some bugs introduced colors like rgba('#abcdef00'), which are not valid
function fixInvalidColor(color) {
  if (typeof color !== 'string') {
    return ''
  }
  const startHex = color.indexOf('#')
  if (startHex === -1) {
    return color
  }
  const endHex = color.indexOf(')')
  if (endHex === -1) {
    return color
  }
  return color.substr(startHex, endHex - startHex)
}

/**
 * Check if the current device is mobile based on session storage
 * @returns {boolean} true if device is mobile
 */
function isMobile() {
  return sessionStorage.getItem('is_mobile') === '1'
}

/**
 * Check if the current device is desktop based on session storage
 * @returns {boolean} true if device is desktop
 */
function isDesktop() {
  return sessionStorage.getItem('is_desktop') === '1'
}

export {
  fixInvalidColor,
  setCampaignDefaults,
  replacePredefinedVariables,
  isMobile,
  isDesktop,
  defaultCampaign,
  defaultConnectionCheckConfig,
}
